import { ReactNode, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useMaintenanceMode } from "@/hooks/useMaintenanceMode";
import { useAdminRole } from "@/hooks/useAdminRole";
import { supabase } from "@/integrations/supabase/client";
import { Helmet } from "react-helmet-async";
import { Wrench, PauseCircle } from "lucide-react";
import hittadelarLogo from "@/assets/hittadelar-logo-main.png";

/**
 * Full-page maintenance screen shown to visitors while maintenance mode
 * is active. Matches the site's visual identity (blue primary, system fonts).
 */
const MaintenancePage = () => (
  <div className="min-h-screen bg-background flex flex-col items-center justify-center px-6 text-center">
    <Helmet>
      <title>Planerat underhåll – HittaDelar</title>
      <meta name="description" content="HittaDelar genomgår just nu planerat underhåll. Återkom gärna lite senare." />
      <meta name="robots" content="noindex" />
    </Helmet>

    <img
      src={hittadelarLogo}
      alt="HittaDelars logotyp"
      className="h-16 w-auto mb-10"
    />

    <div className="rounded-full bg-primary/10 p-5 mb-6">
      <Wrench className="h-10 w-10 text-primary" />
    </div>

    <h1 className="text-3xl font-bold mb-3">Planerat underhåll</h1>
    <p className="text-muted-foreground max-w-md mb-8">
      Vi uppdaterar HittaDelar just nu för att göra tjänsten ännu bättre.
      Återkom gärna lite senare – vi är snart tillbaka.
    </p>

    <div className="rounded-lg border border-primary/30 bg-primary/5 px-6 py-4 max-w-md mb-4 flex items-start gap-3 text-left">
      <PauseCircle className="h-5 w-5 text-primary mt-0.5 shrink-0" />
      <p className="text-sm">
        <span className="font-medium">Ingen tid räknas ner just nu.</span>{" "}
        <span className="text-muted-foreground">
          Har du t.ex. 72 timmar på dig att svara eller reklamera står den tiden still medan sajten
          är avstängd – och fortsätter när vi är tillbaka.
        </span>
      </p>
    </div>

    <div className="rounded-lg border bg-card px-6 py-4 max-w-md">
      <p className="text-sm text-muted-foreground">
        Har du en pågående affär eller fråga? Kontakta oss på{" "}
        <a href="mailto:hittadelar@gmail.com" className="text-primary font-medium hover:underline">
          hittadelar@gmail.com
        </a>
      </p>
    </div>

    <p className="mt-12 text-sm text-muted-foreground">© HittaDelar</p>
  </div>
);

/**
 * Wraps the whole app. When maintenance mode is enabled, everyone except
 * admins sees the maintenance page — admins keep full access so they can
 * work on the site and turn the mode off again.
 */
const MaintenanceGate = ({ children }: { children: ReactNode }) => {
  const { maintenanceMode, loading: maintenanceLoading } = useMaintenanceMode();
  const { isAdmin, loading: roleLoading } = useAdminRole();
  const location = useLocation();

  // The login page stays reachable during maintenance so an admin who gets
  // logged out can always sign back in and turn maintenance mode off again.
  const isAuthPage = location.pathname.startsWith("/auth");

  const showMaintenance =
    !maintenanceLoading && !roleLoading && maintenanceMode && !isAdmin && !isAuthPage;

  // Log out non-admin users while maintenance mode is active.
  useEffect(() => {
    if (!showMaintenance) return;
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      // Double-check against the database that the user really is not an
      // admin before signing out — never log out an admin by mistake.
      try {
        const { data: isAdminDb } = await supabase.rpc("has_role", {
          _user_id: session.user.id,
          _role: "admin",
        });
        if (isAdminDb) return;
      } catch {
        // If the check fails, stay on the safe side and keep the session.
        return;
      }
      await supabase.auth.signOut().catch(() => {
        // Ignore errors – the maintenance page is shown regardless.
      });
    })();
  }, [showMaintenance]);

  if (maintenanceLoading || roleLoading) return <>{children}</>;
  if (showMaintenance) return <MaintenancePage />;
  return <>{children}</>;
};

export default MaintenanceGate;
